import React, { useState, useCallback } from "react";
import {
  Modal,
  Upload,
  message,
  Row,
  Col,
  Image,
  Spin,
  Typography,
  Card,
  Divider,
  Descriptions,
  Button,
} from "antd";
import { InboxOutlined, FilePdfOutlined, EyeOutlined } from "@ant-design/icons";
import Compressor from "compressorjs";
import axios from "axios";
import ReadPdfReceiptLawsuit from "../hooks/ReadPdfReceiptLawsuit";

const { Dragger } = Upload;
const { Title, Text } = Typography;

// ── PDF Preview Modal ─────────────────────────────────
const PdfPreviewModal = ({ open, url, onClose }) => (
  <Modal
    open={open}
    onCancel={onClose}
    footer={null}
    width="90vw"
    style={{ top: 10 }}
    title="ข้อมูล PDF"
  >
    <iframe
      src={url}
      style={{ width: "100%", height: "80vh", border: "none" }}
      title="pdf-preview"
    />
  </Modal>
);

const AddImage = ({
  visible,
  onCancel,
  onSuccess,
  loanData,
  imageType = 4, //อย่าลืมเปลี่ยน
  apiUrl,
  headers,
  title = "อัปโหลดใบเสร็จศาล",
  handleDataPdf,
}) => {
  const [fileList, setFileList] = useState([]);
  const [allImg, setAllImg] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [imgData, setImgData] = useState(null);

  // ✅ PDF Preview state
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState("");

  const { uploadToApi, data, loadingReadPdfReceiptLawsuit, error } =
    ReadPdfReceiptLawsuit();

  console.log(loanData);

  const handleUpload = async (file) => {
    setUploading(true);
    const result = await uploadToApi(file);
    setImgData(result);
    if (result) {
      message.success("สามารถอ่านไฟล์ PDF ได้");
    } else {
      message.error("ไม่สามารถอ่านไฟล์ PDF ได้");
    }
    setUploading(false);
  };

  const compressImage = async (file, types, index) => {
    return new Promise((resolve) => {
      let quality;
      if (file.size >= 2500000) quality = 0.1;
      else if (file.size >= 2000000) quality = 0.15;
      else if (file.size >= 1500000) quality = 0.2;
      else if (file.size >= 1000000) quality = 0.3;
      else quality = 0.5;

      new Compressor(file, {
        quality,
        success: (result) => {
          const compressedFileAsFile = new File([result], file.name, {
            type: result.type,
            lastModified: Date.now(),
          });
          setAllImg((prev) => [
            ...prev,
            {
              imageCompressedFile: compressedFileAsFile,
              type: types,
              originalIndex: index,
            },
          ]);
          setUploading(false);
          resolve(true);
        },
        error: (err) => {
          console.error("Compressor error:", err);
          message.error(`ไม่สามารถบีบอัดไฟล์ "${file.name}" ได้`);
          setUploading(false);
          resolve(true);
        },
      });
    });
  };

  const getBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  // ✅ Preview — รูปใช้ Modal, PDF ใช้ iframe
  const handlePreview = async (file) => {
    const isPdf =
      file.type === "application/pdf" ||
      file.name.toLowerCase().endsWith(".pdf");

    if (isPdf) {
      const url =
        file.url ||
        (file.originFileObj ? URL.createObjectURL(file.originFileObj) : null);
      if (url) {
        setPdfPreviewUrl(url);
        setPdfPreviewOpen(true);
      }
      return;
    }

    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
    setPreviewImage(file.url || file.preview);
    setPreviewOpen(true);
  };

  const handleChangeIMG = ({ fileList: newFileList, file }) => {
    setFileList(newFileList);
    if (file.status === "removed") {
      setAllImg((prev) => prev.filter((img) => img.originalIndex !== file.uid));
      if (newFileList.length === 0) {
        setImgData(null);
      }
    }
    if (["done", "removed", "error"].includes(file.status)) {
      setUploading(false);
    }
  };

  const beforeUpts = async (file, ty) => {
    const isPdf =
      file.type === "application/pdf" ||
      file.name.toLowerCase().endsWith(".pdf");

    if (isPdf) {
      handleUpload(file);
      setAllImg((prev) => [
        ...prev,
        { imageCompressedFile: file, type: ty, originalIndex: file.uid },
      ]);
      return false;
    }

    if (file.size > 5000000) {
      message.warning(`ไฟล์ "${file.name}" เกิน 5MB`);
      return Upload.LIST_IGNORE;
    }

    try {
      setUploading(true);
      await compressImage(file, ty, file.uid);
      return false;
    } catch (error) {
      message.error("File processing failed.");
      return Upload.LIST_IGNORE;
    }
  };

  const uploadProps = {
    name: "file",
    multiple: true,
    accept: "image/*,.pdf",
    listType: "picture",
    fileList,
    onPreview: handlePreview,
    onChange: handleChangeIMG,
    beforeUpload: (file) => beforeUpts(file, imageType),
    iconRender: (file) =>
      file.type === "application/pdf" || file.name.endsWith(".pdf") ? (
        <FilePdfOutlined style={{ fontSize: "48px", color: "#ff4d4f" }} />
      ) : null,
  };

  const handleUpLoadIMG = useCallback(async () => {
    if (allImg.length === 0) {
      message.warning("กรุณาเลือกไฟล์ที่ต้องการอัปโหลด");
      return;
    }
    setUploading(true);
    const newFormData = new FormData();
    try {
      if (loanData?.loanId) newFormData.append("id", loanData.loanId); // loanData.loanId
      if (loanData?.lawsuitId)
        newFormData.append("lawsuitId", loanData.lawsuitId);
      newFormData.append("notis", 5);

      const sortedImgs = allImg
        .filter((img) => img.originalIndex !== undefined)
        .sort((a, b) => a.type - b.type || a.originalIndex - b.originalIndex);

      for (const file of sortedImgs) {
        newFormData.append("file", file.imageCompressedFile);
        newFormData.append("menuType", 5); //อย่าลืมเปลี่ยน 5
        newFormData.append("imageType", file.type); //อย่าลืมเปลี่ยน file.type
      }
      console.log("newFormData", newFormData);

      const response = await axios.post(apiUrl, newFormData, { headers });
      if (response.status === 200) {
        message.success(`อัปโหลดสำเร็จ ${allImg.length} ไฟล์`);
        setFileList([]);
        setAllImg([]);
        onSuccess?.(response.data);
        handleDataPdf(imgData, loanData);
        setImgData(null);
      }
    } catch (error) {
      message.error("เกิดข้อผิดพลาดในการอัปโหลด");
    } finally {
      setUploading(false);
    }
  }, [allImg, apiUrl, headers, loanData, onSuccess, handleDataPdf, imgData]);

  const handleCancel = () => {
    setFileList([]);
    setAllImg([]);
    setPreviewOpen(false);
    setPreviewImage("");
    setImgData(null);
    setPdfPreviewOpen(false);
    setPdfPreviewUrl("");
    onCancel?.();
  };

  const ReceiptResult = ({ data }) => {
    if (!data) return null;
    return (
      <Card
        bordered={false}
        style={{ width: "100%", background: "#f9f9f9", marginTop: 16 }}
      >
        <Descriptions title="ข้อมูลจาก PDF" bordered column={2}>
          <Descriptions.Item label="เลขคดี">{data.case_no}</Descriptions.Item>

          <Descriptions.Item label="ยอดรวมทั้งหมด" span={2}>
            <Title level={4} type="success" style={{ margin: 0 }}>
              {data.grand_total?.toLocaleString()} บาท
            </Title>
          </Descriptions.Item>
          <Descriptions.Item label="ศาล">{data.court}</Descriptions.Item>
          <Descriptions.Item label="วันที่">{data.date}</Descriptions.Item>
        </Descriptions>

        <Divider orientation="left">รายละเอียดแต่ละหน้า</Divider>

        {data.pages?.map((page) => (
          <Card
            key={page.page}
            size="small"
            title={`หน้า ${page.page}`}
            style={{ marginBottom: 12 }}
            extra={
              <Text strong>
                รวมหน้า: {page.total_amount?.toLocaleString()} บาท
              </Text>
            }
          >
            {page.items?.map((item, index) => (
              <div
                key={index}
                style={{ display: "flex", justifyContent: "space-between" }}
              >
                <Text>{item.item_name}</Text>
                <Text>{item.amount?.toLocaleString()} บาท</Text>
              </div>
            ))}
          </Card>
        ))}
      </Card>
    );
  };

  return (
    <>
      <Modal
        title={`${title} - ${loanData?.CONTNO || ""}`}
        open={visible}
        onOk={handleUpLoadIMG}
        onCancel={handleCancel}
        okText="อัปโหลด"
        cancelText="ยกเลิก"
        width={700}
        confirmLoading={uploading}
        okButtonProps={{ disabled: fileList.length === 0 }}
      >
        <Spin spinning={uploading} size="large" tip="กำลังโหลดข้อมูล...">
          <div style={{ marginBottom: 16 }}>
            {loanData?.CONTNO && (
              <p>
                สัญญา: <strong>{loanData.CONTNO}</strong>
              </p>
            )}
            {loanData?.SNAM && (
              <p>
                ชื่อลูกหนี้:{" "}
                <strong>
                  {loanData.SNAM} {loanData.NAME1} {loanData.NAME2}
                </strong>
              </p>
            )}
            <p style={{ color: "#666", fontSize: 12 }}>
              รองรับไฟล์: JPG, PNG, PDF (ขนาดไม่เกิน 5MB)
            </p>
          </div>

          <Dragger {...uploadProps} style={{ width: "100%", minHeight: 200 }}>
            <p className="ant-upload-drag-icon">
              <InboxOutlined style={{ color: "blue", fontSize: 48 }} />
            </p>
            <p className="ant-upload-text" style={{ fontSize: 16 }}>
              คลิกหรือลากไฟล์มาวางที่นี่
            </p>
            <p className="ant-upload-hint" style={{ fontSize: 12 }}>
              รองรับการอัปโหลดหลายไฟล์พร้อมกัน
            </p>
          </Dragger>

          {fileList.length > 0 && (
            <p style={{ fontWeight: "bold", marginTop: 16 }}>
              ไฟล์ที่เลือก: {fileList.length} ไฟล์
            </p>
          )}

          {/* ✅ Preview grid */}
          {fileList.length > 0 && (
            <Row gutter={[8, 8]} style={{ marginTop: 16 }}>
              {fileList.map((file) => {
                const isPdf =
                  file.type === "application/pdf" ||
                  file.name.toLowerCase().endsWith(".pdf");
                const previewUrl = file.originFileObj
                  ? URL.createObjectURL(file.originFileObj)
                  : file.url;

                return (
                  <Col key={file.uid} xs={8} sm={6}>
                    <div
                      style={{
                        border: "1px solid #d9d9d9",
                        borderRadius: 8,
                        padding: 8,
                        textAlign: "center",
                        position: "relative",
                      }}
                    >
                      {isPdf ? (
                        <>
                          <FilePdfOutlined
                            style={{ fontSize: 40, color: "#ff4d4f" }}
                          />
                          {/* ✅ ปุ่ม Preview PDF */}
                          <Button
                            size="small"
                            icon={<EyeOutlined />}
                            style={{
                              marginTop: 4,
                              width: "100%",
                              color: "blue",
                            }}
                            onClick={() => {
                              setPdfPreviewUrl(previewUrl);
                              setPdfPreviewOpen(true);
                            }}
                          >
                            ดู PDF
                          </Button>
                        </>
                      ) : (
                        file.originFileObj && (
                          <img
                            src={previewUrl}
                            alt={file.name}
                            style={{
                              width: "100%",
                              height: 80,
                              objectFit: "cover",
                              borderRadius: 4,
                            }}
                          />
                        )
                      )}
                      <p
                        style={{
                          fontSize: 10,
                          marginTop: 4,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={file.name}
                      >
                        {file.name}
                      </p>
                    </div>
                  </Col>
                );
              })}
            </Row>
          )}

          {/* ✅ แสดงข้อมูล PDF ที่ parse ได้ */}
          {imgData && <ReceiptResult data={imgData} />}
        </Spin>
      </Modal>

      {/* ✅ Image Preview */}
      <Image
        style={{ display: "none" }}
        preview={{
          visible: previewOpen,
          onVisibleChange: (v) => setPreviewOpen(v),
          src: previewImage,
        }}
      />

      {/* ✅ PDF Preview Modal */}
      <PdfPreviewModal
        open={pdfPreviewOpen}
        url={pdfPreviewUrl}
        onClose={() => {
          setPdfPreviewOpen(false);
          setPdfPreviewUrl("");
        }}
      />
    </>
  );
};

export default AddImage;
